import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterBackupContent = () => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [options, setOptions] = useState({
    include_orders: true,
    include_prospection: true
  });

  useEffect(() => {
    loadBackupInfo();
  }, []);

  const loadBackupInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/backup/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupInfo(response.data);
    } catch (err) {
      console.error('Error loading backup info:', err);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/backup/export`, options, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 120000
      });

      // Download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']
        ?.split('filename=')[1] || `backup_pedeefacil_${new Date().toISOString().slice(0,10)}.zip`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Backup exportado com sucesso!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Erro ao exportar backup');
    }
    setLoading(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error('Selecione um arquivo .zip');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Arquivo muito grande (max 100MB)');
      return;
    }

    setImporting(true);
    setImportResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/master/backup/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      setImportResult(response.data);
      toast.success(`Backup importado! ${response.data.summary.total_imported} documentos.`);
      loadBackupInfo();
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || 'Erro ao importar backup');
    }
    setImporting(false);
    e.target.value = '';
  };

  return (
    <div className="space-y-6" data-testid="master-backup-content">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Backup e Restaura\u00E7\u00E3o</h2>
          <p className="text-gray-400 text-sm mt-1">Exporte e importe dados entre ambientes</p>
        </div>
      </div>

      {/* Info do banco */}
      {backupInfo && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-3">Dados Atuais</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">{backupInfo.total_restaurants}</p>
              <p className="text-gray-400 text-xs">Restaurantes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">{backupInfo.total_documents}</p>
              <p className="text-gray-400 text-xs">Documentos Total</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-400">{backupInfo.collections?.products || 0}</p>
              <p className="text-gray-400 text-xs">Produtos</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">{backupInfo.collections?.orders || 0}</p>
              <p className="text-gray-400 text-xs">Pedidos</p>
            </div>
          </div>
        </div>
      )}

      {/* Exportar */}
      <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold text-lg mb-2">Exportar Backup</h3>
        <p className="text-gray-300 text-sm mb-4">
          Gera um arquivo ZIP com todos os dados das lojas, card\u00E1pios, configura\u00E7\u00F5es e clientes.
        </p>
        
        <div className="space-y-3 mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.include_orders}
              onChange={(e) => setOptions({...options, include_orders: e.target.checked})}
              className="w-4 h-4 rounded accent-blue-500"
            />
            <span className="text-white text-sm">Incluir pedidos (hist\u00F3rico)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.include_prospection}
              onChange={(e) => setOptions({...options, include_prospection: e.target.checked})}
              className="w-4 h-4 rounded accent-blue-500"
            />
            <span className="text-white text-sm">Incluir dados de prospec\u00E7\u00E3o</span>
          </label>
        </div>

        <button
          onClick={handleExport}
          disabled={loading}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          data-testid="backup-export-button"
        >
          {loading ? (
            <>
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              Exportando...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              Exportar Backup (ZIP)
            </>
          )}
        </button>
      </div>

      {/* Importar */}
      <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
        <h3 className="text-green-400 font-semibold text-lg mb-2">Importar Backup</h3>
        <p className="text-gray-300 text-sm mb-4">
          Restaura dados de um backup exportado anteriormente. Dados existentes N\u00C3O s\u00E3o sobrescritos.
        </p>

        <label className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors cursor-pointer" data-testid="backup-import-button">
          {importing ? (
            <>
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              Importando...
            </>
          ) : (
            <>
              <Upload className="w-5 h-5" />
              Selecionar Arquivo ZIP
            </>
          )}
          <input
            type="file"
            accept=".zip"
            onChange={handleImport}
            className="hidden"
            disabled={importing}
          />
        </label>
      </div>

      {/* Resultado da importa\u00E7\u00E3o */}
      {importResult && (
        <div className="bg-white/5 border border-green-500/30 rounded-xl p-5">
          <h3 className="text-green-400 font-semibold mb-3">Resultado da Importa\u00E7\u00E3o</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">{importResult.summary.total_imported}</p>
              <p className="text-gray-400 text-xs">Importados</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">{importResult.summary.total_skipped}</p>
              <p className="text-gray-400 text-xs">Ignorados (j\u00E1 existiam)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-400">{importResult.summary.total_errors}</p>
              <p className="text-gray-400 text-xs">Erros</p>
            </div>
          </div>
          
          <details className="text-sm">
            <summary className="text-gray-400 cursor-pointer hover:text-white">Ver detalhes por cole\u00E7\u00E3o</summary>
            <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
              {Object.entries(importResult.details).map(([coll, data]) => (
                <div key={coll} className="flex justify-between text-gray-300 px-2 py-1 bg-white/5 rounded">
                  <span>{coll}</span>
                  <span className="text-green-400">{data.imported} imp</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Informa\u00E7\u00F5es */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3">Informa\u00E7\u00F5es do Backup</h3>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-start gap-2"><span className="text-blue-400">*</span> Formato: JSON compactado em ZIP (port\u00E1til entre agentes)</li>
          <li className="flex items-start gap-2"><span className="text-blue-400">*</span> Senhas: Exportadas como hash (lojistas n\u00E3o precisam redefinir)</li>
          <li className="flex items-start gap-2"><span className="text-blue-400">*</span> Imagens: URLs do Cloudinary (continuam acess\u00EDveis)</li>
          <li className="flex items-start gap-2"><span className="text-blue-400">*</span> Importa\u00E7\u00E3o: Dados existentes N\u00C3O s\u00E3o sobrescritos</li>
          <li className="flex items-start gap-2"><span className="text-blue-400">*</span> Compat\u00EDvel: Entre qualquer inst\u00E2ncia do Pedeefacil v{backupInfo?.version || '1.0'}</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================
// CONSUMO DE IA POR LOJISTA (Master)
// ============================================================
